import type { FieldValues } from "react-hook-form";
import { Skeleton } from "@shared-kernel/primary/shared/shadcn/ui/skeleton";
import { LONG_TEXT_ROWS } from "@shared-kernel/primary/shared/long-text-field/long-text-rows";
import type { FieldConfig } from "./types";

interface SkeletonFieldRendererProps<T extends FieldValues> {
  field: FieldConfig<T>;
}

/** `text-sm` tient sur 20 px de hauteur de ligne, plus les 16 px de padding et les 2 px de bordure. */
const textareaHeightFor = (rows: number = LONG_TEXT_ROWS) => `${rows * 20 + 18}px`;

export function SkeletonFieldRenderer<T extends FieldValues>({ field }: SkeletonFieldRendererProps<T>) {
  const labelWidth = field.skeleton?.labelWidth ?? "w-24";

  // for label to match actual FormLabel line-height
  const labelHeight = "h-6";

  switch (field.type) {
    case "text":
    case "email":
    case "password":
    case "number":
    case "select":
    case "combobox":
    case "date":
    case "display":
      return (
        <div className="space-y-2">
          <Skeleton className={`${labelHeight} ${labelWidth}`} />
          <Skeleton className="h-10 w-full" />
        </div>
      );

    case "textarea":
      return (
        <div className="space-y-2">
          <Skeleton className={`${labelHeight} ${labelWidth}`} />
          {/* La hauteur se déduit du `rows` du champ : elle ne se dit qu'une fois, dans sa config. */}
          <Skeleton className="w-full" style={{ height: textareaHeightFor(field.rows) }} />
        </div>
      );

    case "checkbox":
      return (
        <div className="flex items-center space-x-3">
          <Skeleton className="size-4 rounded-sm" />
          <Skeleton className={`${labelHeight} ${labelWidth}`} />
        </div>
      );

    case "custom":
      return field.renderSkeleton();

    default:
      return null;
  }
}
